import { History } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { computeDiffStatus } from '~/components/common/enrichment-diff-table/diff-status.util';
import { FullPagination } from '~/components/common/full-pagination/full-pagination';
import { SearchInput } from '~/components/common/search-input';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { Switch } from '~/components/ui/switch';
import { ProductDeliveryLogSheet } from '~/features/deliveries/components/product-log/product-delivery-log-sheet';
import { useTenantAdmin } from '~/hooks/useTenantAdmin';

import { CatalogOperationsLayout } from '../components/catalog-operations-layout';
import { DeliveryCompareAttributeRow } from '../components/delivery-compare/delivery-compare-attribute-row';
import { DeliveryCompareBlocked } from '../components/delivery-compare/delivery-compare-blocked';
import { DeliveryCompareHeader } from '../components/delivery-compare/delivery-compare-header';
import { DeliveryCompareProductRail } from '../components/delivery-compare/delivery-compare-product-rail';
import { DeliveryCompareProductStrip } from '../components/delivery-compare/delivery-compare-product-strip';
import { DeliveryCompareRunView } from '../components/delivery-compare/delivery-compare-run-view';
import { DeliveryCompareTableHeader } from '../components/delivery-compare/delivery-compare-table-header';
import { DeliveryStatusLegend } from '../components/delivery-compare/delivery-status-legend';
import { useBatchConnectionId } from '../hooks/use-batch-connection-id';
import { useBatchHeader } from '../hooks/use-batch-detail';
import {
  buildSelectedOptimizations,
  useDeliveryCompareRail,
  useDeliveryCompareRailOutcomes,
  useDeliveryCompareSelectedDelivery,
  usePreEnrichmentSnapshot,
  useSelectedEnrichment,
} from '../hooks/use-delivery-compare';
import type { Product } from '../types/catalog-operations.types';
import { getDeliveryCompareEligibility } from '../utils/delivery-compare-eligibility';
import {
  type DeliveredFieldStatus,
  deliveredFieldError,
  deliveredFieldStatus,
  extractDeliveredValue,
  tallyDeliveredFieldStatuses,
} from '../utils/delivery-field-keys';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;

interface Props {
  batchId: string;
}

/**
 * Pre-enrichment vs enriched vs delivered, side by side, for one delivered
 * batch. See `docs/delivery-compare-view.md` for the data-source and id-join
 * decisions this page is built on.
 */
export function DeliveryCompareView({ batchId }: Props) {
  const { t } = useTranslation('catalog-operations');
  const { data: header, isLoading: headerLoading } = useBatchHeader(batchId);
  // Admins (subsidiary admin + super-admin) can switch to the Temporal-style
  // delivery-run view; members only ever see the comparison.
  const isTenantAdmin = useTenantAdmin();
  const [view, setView] = useState<'compare' | 'run'>('compare');
  // Product delivery log sheet for the selected product (admins).
  const [logOpen, setLogOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [changedOnly, setChangedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<DeliveredFieldStatus | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  // The status legend's counts are per-product — clear a stale filter when
  // the selection changes rather than silently hiding every row on the next
  // product because it has no attribute in the previously-active status.
  useEffect(() => {
    setStatusFilter(null);
  }, [selectedId]);

  // 500ms debounce per project convention — the products list is server-paged/searched.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const { connectionId } = useBatchConnectionId(header?.connectorCode);

  const rail = useDeliveryCompareRail(
    header?.curationRefId,
    batchId,
    page,
    PAGE_SIZE,
    debouncedSearch,
  );
  const products = rail.data?.products ?? [];

  // Auto-select the first row of a page whenever the page's product set
  // changes and nothing on it is currently selected.
  useEffect(() => {
    if (products.length === 0) return;
    if (!products.some((p) => p.id === selectedId)) {
      setSelectedId(products[0]?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the page's product set changes
  }, [products]);

  const selectedProduct: Product | undefined = products.find((p) => p.id === selectedId);

  const railOutcomes = useDeliveryCompareRailOutcomes(
    connectionId,
    header?.deliveryJobId,
    products,
  );

  // The enrichment endpoint's `connectorCode` query param wants the SHORT form
  // (`GMC`/`META`, defaulting to GMC when omitted) — never the long form used
  // to match connections (`GOOGLE_MERCHANT`/`META_COMMERCE`). Derived the same
  // way `product-detail-page.tsx` does for the identical endpoint, rather than
  // trusting `header.connectorCode` verbatim.
  const enrichmentConnectorCode = header?.useCase === 'META_ADS' ? 'META' : undefined;

  const preEnrichment = usePreEnrichmentSnapshot(selectedProduct, batchId);
  const enrichment = useSelectedEnrichment(selectedProduct, enrichmentConnectorCode);
  const optimizations = buildSelectedOptimizations(enrichment.data?.optimizations, preEnrichment);

  const delivery = useDeliveryCompareSelectedDelivery(
    connectionId,
    header?.deliveryJobId,
    selectedProduct,
  );
  const deliveredCount = optimizations.filter(
    (opt) => deliveredFieldStatus(opt.field, delivery.row) === 'sent',
  ).length;
  const statusCounts = tallyDeliveredFieldStatuses(
    optimizations.map((opt) => opt.field),
    delivery.row,
  );
  const visibleOptimizations = optimizations.filter((opt) => {
    if (changedOnly && computeDiffStatus(opt.currentValue, opt.value) === 'unchanged') return false;
    if (statusFilter && deliveredFieldStatus(opt.field, delivery.row) !== statusFilter)
      return false;
    return true;
  });

  if (headerLoading) {
    return (
      <CatalogOperationsLayout testId="delivery-compare-layout">
        <div className="space-y-4" data-testid="delivery-compare-loading">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </CatalogOperationsLayout>
    );
  }

  if (!header) {
    return (
      <CatalogOperationsLayout testId="delivery-compare-layout">
        <DeliveryCompareBlocked reason="not_delivered" batchId={batchId} />
      </CatalogOperationsLayout>
    );
  }

  const eligibility = getDeliveryCompareEligibility(header);
  if (!eligibility.eligible) {
    return (
      <CatalogOperationsLayout testId="delivery-compare-layout">
        <DeliveryCompareHeader header={header} batchId={batchId} anyMockRows={false} />
        <div className="mt-6">
          <DeliveryCompareBlocked
            reason={eligibility.reason ?? 'not_delivered'}
            batchId={batchId}
          />
        </div>
      </CatalogOperationsLayout>
    );
  }

  const anyMockRows = Array.from(railOutcomes.rowsByCurationId.values()).some(
    (row) => row.runMode === 'MOCK',
  );

  return (
    <CatalogOperationsLayout testId="delivery-compare-layout">
      {/* Only the rail and the attribute rows scroll internally — the
          breadcrumb/title, search, product strip, legend and table header all
          stay on screen, the same pinned-header/scrolling-body split
          `catalog-batch-detail-page.tsx` already uses. */}
      <div className="flex h-full min-h-0 flex-col" data-testid="delivery-compare-page">
        <div className="shrink-0 space-y-4 pb-6">
          <DeliveryCompareHeader header={header} batchId={batchId} anyMockRows={anyMockRows} />
          <div className="flex flex-wrap items-center gap-3">
            {isTenantAdmin && (
              <SegmentedTabs
                value={view}
                onChange={setView}
                ariaLabel={t('deliveryCompare.views.ariaLabel')}
                testId="delivery-compare-view-tabs"
                className="bg-background w-fit border"
                options={[
                  { value: 'compare', label: t('deliveryCompare.views.compare') },
                  { value: 'run', label: t('deliveryCompare.views.run') },
                ]}
              />
            )}
            {view === 'compare' && (
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder={t('deliveryCompare.toolbar.searchPlaceholder')}
                testId="delivery-compare-search"
                className="max-w-sm"
              />
            )}
          </div>
        </div>

        {view === 'run' ? (
          <div
            className="scrollbar-transparent min-h-0 flex-1 overflow-y-auto"
            data-testid="delivery-compare-run-panel"
          >
            <DeliveryCompareRunView runId={header.deliveryJobId} batchId={batchId} />
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
            {/* `minmax(0,1fr)`, not a bare `1fr` — an arbitrary grid track has no
              floor by default, so any wide content in the right column (a long
              attribute value, a long product title) would otherwise push this
              whole track, the grid, and the page past the viewport instead of
              wrapping/truncating inside it. */}
            <div
              className="bg-background flex min-h-0 flex-col rounded-xl border"
              data-testid="delivery-compare-rail-panel"
            >
              <div className="scrollbar-transparent min-h-0 flex-1 overflow-y-auto">
                <DeliveryCompareProductRail
                  testId="delivery-compare-rail"
                  products={products}
                  selectedId={selectedId}
                  onSelect={(p) => {
                    setSelectedId(p.id);
                  }}
                  isLoading={rail.isLoading}
                  outcomesByCurationId={railOutcomes.rowsByCurationId}
                  isLoadingOutcomes={railOutcomes.isLoading}
                />
              </div>
              {rail.data && rail.data.total > PAGE_SIZE ? (
                <div
                  className="shrink-0 border-t p-2"
                  data-testid="delivery-compare-rail-pagination"
                >
                  <FullPagination
                    page={page}
                    pageSize={PAGE_SIZE}
                    totalRows={rail.data.total}
                    onPageChange={setPage}
                    testId="delivery-compare-rail-pager"
                  />
                </div>
              ) : null}
            </div>

            <div className="flex min-h-0 min-w-0 flex-col space-y-4">
              {selectedProduct ? (
                <>
                  <div className="shrink-0 space-y-2">
                    <DeliveryCompareProductStrip
                      product={selectedProduct}
                      row={delivery.row}
                      isLoading={delivery.isLoading}
                      error={delivery.error}
                      deliveredCount={deliveredCount}
                      totalCount={optimizations.length}
                    />
                    {/* Admins: the product's full delivery history across jobs. */}
                    {isTenantAdmin && connectionId ? (
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="cursor-pointer gap-1.5"
                          onClick={() => {
                            setLogOpen(true);
                          }}
                          data-testid="delivery-compare-product-log-button"
                        >
                          <History aria-hidden="true" className="size-3.5" />
                          {t('deliveryCompare.productLog')}
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <div
                    className="bg-background flex min-h-0 flex-1 flex-col rounded-xl border"
                    data-testid="delivery-compare-attribute-table"
                  >
                    {/* Attribute-level controls live inside the same card as what
                      they filter (matching AttributeFilterCard's "filter bar +
                      content, one card, divided by a hairline" pattern), scoped
                      to the selected product — unlike the search above, which
                      filters the product rail instead. */}
                    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                      <DeliveryStatusLegend
                        counts={statusCounts}
                        active={statusFilter}
                        onToggle={(status) => {
                          setStatusFilter((current) => (current === status ? null : status));
                        }}
                      />
                      <label className="text-muted-foreground flex shrink-0 cursor-pointer items-center gap-2 text-sm">
                        <Switch
                          checked={changedOnly}
                          onCheckedChange={setChangedOnly}
                          data-testid="delivery-compare-changed-only-toggle"
                        />
                        {t('deliveryCompare.toolbar.changedOnly')}
                      </label>
                    </div>
                    {enrichment.isLoading || preEnrichment.isLoading ? (
                      <div
                        className="space-y-4 p-4"
                        data-testid="delivery-compare-attribute-table-loading"
                      >
                        {Array.from({ length: 4 }, (_, i) => (
                          <Skeleton key={i} className="h-24 w-full" />
                        ))}
                      </div>
                    ) : visibleOptimizations.length === 0 ? (
                      <p
                        className="text-muted-foreground p-6 text-center text-sm"
                        data-testid="delivery-compare-attribute-table-empty"
                      >
                        {t('deliveryCompare.attributeTable.empty')}
                      </p>
                    ) : (
                      <>
                        <div className="shrink-0 px-4 pt-3">
                          <DeliveryCompareTableHeader />
                        </div>
                        <div
                          className="scrollbar-transparent min-h-0 flex-1 overflow-y-auto px-4 pb-4"
                          data-testid="delivery-compare-attribute-list"
                        >
                          {visibleOptimizations.map((opt) => {
                            const status = deliveredFieldStatus(opt.field, delivery.row);
                            const errorMessage = deliveredFieldError(opt.field, delivery.row);
                            const deliveredValue = extractDeliveredValue(opt.field, delivery.row);
                            return (
                              <DeliveryCompareAttributeRow
                                key={opt.field}
                                opt={opt}
                                deliveryStatus={status}
                                {...(errorMessage ? { deliveryError: errorMessage } : {})}
                                {...(deliveredValue ? { deliveredValue } : {})}
                              />
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div
                  className="bg-background text-muted-foreground flex h-64 items-center justify-center rounded-xl border text-sm"
                  data-testid="delivery-compare-no-selection"
                >
                  {t('deliveryCompare.noSelection')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {isTenantAdmin && connectionId && selectedProduct ? (
        <ProductDeliveryLogSheet
          open={logOpen}
          onOpenChange={setLogOpen}
          connectionId={connectionId}
          connectorCode={header.connectorCode}
          idType="source"
          subject={{
            productId: selectedProduct.id,
            sku: selectedProduct.sku,
            title: selectedProduct.title,
            imageUrl: selectedProduct.imageUrl ?? null,
          }}
          focusJobId={header.deliveryJobId}
        />
      ) : null}
    </CatalogOperationsLayout>
  );
}
